import React from "react";
import type { CustomerMarginTrendItem } from "../type";
import { colors } from "../../../utils/colors";
import { useApp } from "../../../contexts/AppContext";
import { GenericBarChart, GenericBarChartRow } from "./GenericBarChart";
import {
  CUSTOMER_MARGIN_SERIES_NAME,
  EMPTY_CUSTOMER_MARGIN_STATE_MESSAGE,
} from "../../../utils/consts";

const monthShort = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const toChartRows = (series: CustomerMarginTrendItem[]) =>
  series.map((p) => ({
    label: `${monthShort[p.month - 1] ?? p.month} ${p.year}`,
    value: Number(p.customer_margin),
  }));

const formatMarginCurrency = (amount: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);

export interface CustomerMarginTrendChartProps {
  title?: string;
  loading?: boolean;
  series: CustomerMarginTrendItem[];
}

export const CustomerMarginTrendChart: React.FC<
  CustomerMarginTrendChartProps
> = ({ title, loading = false, series }) => {
  const { drawerOpen } = useApp();

  const rows: GenericBarChartRow[] = toChartRows(series);

  return (
    <GenericBarChart
      title={title}
      data={rows}
      seriesName={CUSTOMER_MARGIN_SERIES_NAME}
      loading={loading}
      height={400}
      minHeight={400}
      emptyMessage={EMPTY_CUSTOMER_MARGIN_STATE_MESSAGE}
      legendTop={8}
      legendRight={50}
      barWidth="35%"
      xAxisLabelColor={colors.black}
      xAxisLabelFontSize="0.625rem"
      xAxisLabelFontWeight={500}
      yAxisLabelColor={colors.chart_gray}
      yAxisLabelFontSize="0.6875rem"
      xAxisRotateThreshold={5}
      defaultBarColor={colors.bar_lavender}
      axisTooltipFormatter={({
        dataIndex,
        categoryLabel,
        primaryValue,
        seriesMarkerHtml,
      }) => {
        const point = series[dataIndex];
        const currency = formatMarginCurrency(primaryValue);
        const pct = point
          ? `${Number(point.margin_pct).toFixed(2)}%`
          : "N/A";
        return `<div style="color:${colors.chart_text};">${categoryLabel}<br/><span style="display:inline-block;margin-top:6px;">${seriesMarkerHtml}${CUSTOMER_MARGIN_SERIES_NAME} <span style="display:inline-block;padding-left:14px;"><strong>${currency}</strong> (<strong>${pct}</strong>)</span></span></div>`;
      }}
      yAxisValueFormatter={(val: number) =>
        val >= 1_000_000
          ? `${(val / 1_000_000).toFixed(2)}M`
          : Number(val).toFixed(2)
      }
      resizeTrigger={drawerOpen}
    />
  );
};
